import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
    Zap, Phone, MessageSquare, Copy, Share2, CheckCircle2, Heart, Users, Mail, Facebook,
} from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { BRAND, BUSINESS_PHONE, BUSINESS_PHONE_FORMATTED, OWNER_FIRST_NAME } from "@/lib/constants";

export default function SharePage() {
    const { code } = useParams();
    const [info, setInfo] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [copied, setCopied] = useState(false);

    const refUrl = `${window.location.origin}/ref/${code}`;

    useEffect(() => {
        document.title = `${OWNER_FIRST_NAME} helped me save · ${BRAND}`;
        http.get(`/public/share/${code}`)
            .then(({ data }) => setInfo(data))
            .catch(() => setNotFound(true));
    }, [code]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(refUrl);
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Could not copy — long-press the link instead");
        }
    };

    const nativeShare = async () => {
        const shareData = {
            title: `${OWNER_FIRST_NAME} helped me save money`,
            text: `${OWNER_FIRST_NAME} walked me through my electric options and I'm paying less now. If you want help too, check this out:`,
            url: refUrl,
        };
        if (navigator.share) {
            try { await navigator.share(shareData); } catch (_) {}
        } else {
            copy();
        }
    };

    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" data-testid="share-404">
                <div className="card-surface p-6 max-w-md text-center">
                    <h1 className="text-xl font-bold text-white">Share link not found</h1>
                    <p className="mt-2 text-sm text-slate-400">
                        That link is invalid or expired. Reach {OWNER_FIRST_NAME} directly at{" "}
                        <a className="text-emerald-300" href={`tel:${BUSINESS_PHONE}`}>{BUSINESS_PHONE_FORMATTED}</a>.
                    </p>
                </div>
            </div>
        );
    }
    if (!info) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

    const smsBody = encodeURIComponent(`${info.first_name} says ${OWNER_FIRST_NAME} helped them save on their bills. Check it out: ${refUrl}`);
    const emailSubject = encodeURIComponent(`${OWNER_FIRST_NAME} helped me save - thought of you`);
    const emailBody = encodeURIComponent(`Hey —\n\nI used ${OWNER_FIRST_NAME} (independent rep in Texas) to review my electric/service options and I'm paying less. He's chill, no pressure.\n\nIf you want to see your options too:\n${refUrl}\n\nOr call/text him: ${BUSINESS_PHONE_FORMATTED}\n\n– ${info.first_name}`);
    const twitterText = encodeURIComponent(`Stopped overpaying for my bills. ${OWNER_FIRST_NAME} walked me through my options - 60 sec, no pressure. ${refUrl}`);
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(refUrl)}`;

    return (
        <div className="min-h-screen" data-testid="share-page">
            <div className="max-w-xl mx-auto px-4 sm:px-6 pt-8 pb-16">
                {/* Branded card */}
                <div className="card-surface p-6 sm:p-8 relative overflow-hidden reveal">
                    <div className="absolute -top-16 -right-16 w-64 h-64 bg-emerald-500/15 blur-3xl rounded-full pointer-events-none" />
                    <div className="relative">
                        <div className="flex items-center gap-2">
                            <span className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/40 inline-flex items-center justify-center text-emerald-300">
                                <Zap className="w-5 h-5" />
                            </span>
                            <span className="font-extrabold text-white text-lg">{BRAND}</span>
                        </div>

                        <div className="mt-6">
                            <div className="chip">
                                <Heart className="w-3 h-3" /> From {info.first_name}
                            </div>
                            <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                                <span className="accent-gradient-text">{OWNER_FIRST_NAME}</span> helped me save.
                            </h1>
                            <p className="mt-3 text-slate-300 leading-relaxed">
                                I'm {info.first_name}{info.state ? ` from ${info.state}` : ""}, and I used {OWNER_FIRST_NAME}
                                {" "}to review my {info.service.toLowerCase()} options. Took a few minutes,
                                no pressure, no fake rates. If anyone you know is overpaying — pass this on.
                            </p>

                            {info.referrals_count > 0 && (
                                <div className="mt-5 inline-flex items-center gap-2 text-xs text-slate-300">
                                    <Users className="w-4 h-4 text-emerald-300" />
                                    {info.referrals_count} friend{info.referrals_count === 1 ? "" : "s"} already helped through me
                                </div>
                            )}
                        </div>

                        {/* QR + ref URL */}
                        <div className="mt-7 rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col sm:flex-row items-center gap-5">
                            <div className="shrink-0 bg-white rounded-xl p-2" data-testid="share-qr">
                                <QRCodeCanvas value={refUrl} size={128} bgColor="#ffffff" fgColor="#0B132B" level="M" includeMargin={false} />
                            </div>
                            <div className="flex-1 min-w-0 w-full">
                                <div className="text-xs text-slate-400 mono uppercase tracking-widest">Your tracked link</div>
                                <div className="mt-1 text-sm text-emerald-300 break-all font-mono" data-testid="share-url">{refUrl}</div>
                                <button
                                    type="button"
                                    onClick={copy}
                                    data-testid="share-copy-btn"
                                    className="btn-ghost mt-3 !py-2 !min-h-0 text-xs w-full"
                                >
                                    {copied ? <><CheckCircle2 className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy link</>}
                                </button>
                            </div>
                        </div>

                        {/* Share buttons */}
                        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2">
                            <button onClick={nativeShare} className="option-tile justify-center !p-3" data-testid="share-native-btn">
                                <Share2 className="w-4 h-4" /> <span className="text-xs">Share</span>
                            </button>
                            <a href={`sms:?&body=${smsBody}`} className="option-tile justify-center !p-3" data-testid="share-sms-btn">
                                <MessageSquare className="w-4 h-4" /> <span className="text-xs">Text</span>
                            </a>
                            <a href={`https://wa.me/?text=${smsBody}`} target="_blank" rel="noopener noreferrer" className="option-tile justify-center !p-3" data-testid="share-whatsapp-btn">
                                <span className="text-sm font-bold">W</span> <span className="text-xs">WhatsApp</span>
                            </a>
                            <a href={fbUrl} target="_blank" rel="noopener noreferrer" className="option-tile justify-center !p-3" data-testid="share-fb-btn">
                                <Facebook className="w-4 h-4" /> <span className="text-xs">Facebook</span>
                            </a>
                            <a href={`mailto:?subject=${emailSubject}&body=${emailBody}`} className="option-tile justify-center !p-3" data-testid="share-email-btn">
                                <Mail className="w-4 h-4" /> <span className="text-xs">Email</span>
                            </a>
                        </div>

                        <div className="mt-7 pt-6 border-t border-white/5">
                            <div className="text-xs text-slate-400 mono uppercase tracking-widest">Reach {OWNER_FIRST_NAME} directly</div>
                            <div className="mt-3 flex flex-wrap gap-3">
                                <a href={`tel:${BUSINESS_PHONE}`} className="btn-primary" data-testid="share-call-cta">
                                    <Phone className="w-4 h-4" /> {BUSINESS_PHONE_FORMATTED}
                                </a>
                                <a href={`sms:${BUSINESS_PHONE}?&body=Hi ${OWNER_FIRST_NAME}, ${info.first_name} sent me.`} className="btn-ghost" data-testid="share-text-jeffrey-cta">
                                    <MessageSquare className="w-4 h-4" /> Text {OWNER_FIRST_NAME}
                                </a>
                            </div>
                            <p className="mt-4 text-xs text-slate-500">
                                Independent representative. Information is provided for comparison purposes only.
                                Plans, rates, fees, terms, eligibility, and availability vary.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 text-center">
                    <Link to="/" className="text-xs text-slate-400 hover:text-emerald-300">
                        Not {info.first_name}? See {OWNER_FIRST_NAME}'s site →
                    </Link>
                </div>
            </div>
        </div>
    );
}
